import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';

import { Button } from '@/components';
import {
  scheduledInstantAPI,
  SiReconciliation,
  SiReconciliationUnassigned,
} from '@/services/api/scheduled-instant';

import { Panel, StatCell } from './shared';

/**
 * Screen 14 "Rekonsiliasi" tab — for one day, reconcile who was ASSIGNED to a
 * slot harian against who actually COMPLETED a delivery.
 *
 * Two answers ops asked for:
 *   - "Ditugaskan, tidak mengirim" — a primary (UTAMA) rider on a roster who
 *     completed no delivery that day.
 *   - "Mengirim, tanpa slot" — a rider who completed deliveries for an SI client
 *     but was on no roster.
 *
 * Scope is deliberate (see the endpoint): only SI-managed providers, UTAMA
 * riders, completion dated in WIB. Completions are read live from
 * delivery-service, so direct (shipment-less) deliveries count too. Defaults to
 * yesterday because same-day deliveries aren't finished yet.
 */
export const ReconciliationView: React.FC = () => {
  const [date, setDate] = useState(
    dayjs().subtract(1, 'day').format('YYYY-MM-DD')
  );
  const [data, setData] = useState<SiReconciliation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // C\S is the heavy read — loaded only when the user asks for it, and reset
  // whenever the date changes.
  const [unassigned, setUnassigned] =
    useState<SiReconciliationUnassigned | null>(null);
  const [unassignedLoading, setUnassignedLoading] = useState(false);
  const [unassignedError, setUnassignedError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setUnassigned(null);
    setUnassignedError(null);
    try {
      setData(await scheduledInstantAPI.reconciliation(date));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [date]);

  const loadUnassigned = useCallback(async () => {
    setUnassignedLoading(true);
    setUnassignedError(null);
    try {
      setUnassigned(await scheduledInstantAPI.reconciliationUnassigned(date));
    } catch (e) {
      setUnassignedError((e as Error).message);
    } finally {
      setUnassignedLoading(false);
    }
  }, [date]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-ink">
            Rekonsiliasi Slot vs Pengiriman
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Rider yang ditugaskan di slot harian vs yang benar-benar
            menyelesaikan pengiriman — {dayjs(date).format('dddd, DD MMM YYYY')}
          </p>
        </div>
        <input
          type="date"
          value={date}
          max={dayjs().subtract(1, 'day').format('YYYY-MM-DD')}
          onChange={(e) => setDate(e.target.value)}
          className="border border-gray-200 rounded-gsm px-3 py-1.5 text-sm text-ink"
        />
      </div>

      {loading && (
        <Panel className="p-10 text-center text-sm text-gray-400">Memuat…</Panel>
      )}

      {!loading && error && (
        <Panel className="p-10 text-center">
          <div className="text-sm text-red-600">{error}</div>
          <Button className="mt-3" onClick={load}>
            Coba lagi
          </Button>
        </Panel>
      )}

      {!loading && !error && data && (
        <>
          <Panel>
            <div className="grid grid-cols-3 divide-x divide-gray-100">
              <StatCell
                label="Ditugaskan (UTAMA)"
                value={data.assignedCount}
                tone="ink"
              />
              <StatCell
                label="Cocok — kirim & di-slot"
                value={data.matchedCount}
                tone="green"
                hint="rider di roster yang menyelesaikan pengiriman"
              />
              <StatCell
                label="Mengirim total"
                value={unassigned ? unassigned.completedCount : '—'}
                tone="ink"
                hint={
                  unassigned
                    ? 'untuk klien yang punya slot hari itu'
                    : 'klik "Tampilkan" di bawah'
                }
              />
            </div>
          </Panel>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <RiderList
              title="Ditugaskan, tidak ada pengiriman"
              tone="amber"
              count={data.assignedNoDelivery.length}
              empty="Semua rider yang ditugaskan menyelesaikan pengiriman."
            >
              {data.assignedNoDelivery.map((r) => (
                <li
                  key={r.riderCode}
                  className="flex items-center justify-between px-5 py-3 border-b border-gray-100 last:border-0"
                >
                  <div>
                    <div className="text-sm font-semibold text-ink">
                      {r.riderName}
                    </div>
                    <div className="text-[11px] text-gray-400 tabular">
                      {r.riderCode}
                    </div>
                  </div>
                  <div className="text-right text-[11px] text-gray-500">
                    <div>{r.clientNames.join(', ')}</div>
                    <div className="tabular">slot {r.earliestWindow}</div>
                  </div>
                </li>
              ))}
            </RiderList>

            {/* C\S is the heavy read — loaded on demand behind a button. */}
            <Panel>
              <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                <span className="text-sm font-bold text-ink">
                  Mengirim, tidak di-assign
                </span>
                {unassigned && (
                  <span
                    className={`text-sm font-bold tabular ${
                      unassigned.deliveredNoSlot.length
                        ? 'text-red-600'
                        : 'text-gray-300'
                    }`}
                  >
                    {unassigned.deliveredNoSlot.length}
                  </span>
                )}
              </div>

              {!unassigned && !unassignedLoading && !unassignedError && (
                <div className="px-5 py-8 text-center">
                  <p className="text-sm text-gray-400 mb-3">
                    Daftar ini berat dihitung — dimuat saat dibutuhkan.
                  </p>
                  <Button onClick={loadUnassigned}>Tampilkan</Button>
                </div>
              )}

              {unassignedLoading && (
                <div className="px-5 py-8 text-center text-sm text-gray-400">
                  Memuat…
                </div>
              )}

              {unassignedError && (
                <div className="px-5 py-8 text-center">
                  <div className="text-sm text-red-600">{unassignedError}</div>
                  <Button className="mt-3" onClick={loadUnassigned}>
                    Coba lagi
                  </Button>
                </div>
              )}

              {unassigned && (
                <>
                  {unassigned.truncated && (
                    <div className="px-5 py-2 text-[11px] text-amber-700 bg-amber-50 border-b border-amber-100">
                      <i className="ri-error-warning-line mr-1" />
                      Daftar mungkin belum lengkap (terlalu banyak pengiriman) —
                      persempit dengan tanggal/klien.
                    </div>
                  )}
                  {unassigned.deliveredNoSlot.length === 0 ? (
                    <div className="px-5 py-8 text-center text-sm text-gray-400">
                      Tidak ada rider yang mengirim tanpa slot.
                    </div>
                  ) : (
                    <ul>
                      {unassigned.deliveredNoSlot.map((r) => (
                        <li
                          key={r.phone}
                          className="flex items-center justify-between px-5 py-3 border-b border-gray-100 last:border-0"
                        >
                          <div>
                            <div className="text-sm font-semibold text-ink">
                              {r.riderName}
                            </div>
                            <div className="text-[11px] text-gray-400 tabular">
                              {r.phone}
                            </div>
                          </div>
                          <div className="text-sm font-bold text-ink tabular">
                            {r.completed} selesai
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </Panel>
          </div>

          <p className="text-[11px] text-gray-400">
            Cakupan: hanya klien yang punya slot Scheduled Instant hari itu,
            rider UTAMA, waktu penyelesaian dihitung WIB. Data pengiriman dibaca
            langsung dari sistem delivery — termasuk pengiriman langsung (tanpa
            shipment). "Mengirim, tidak di-assign" dicocokkan lewat nomor HP.
            Pakai tanggal yang sudah selesai (default kemarin).
          </p>
        </>
      )}
    </div>
  );
};

const RiderList: React.FC<{
  title: string;
  tone: 'amber' | 'red';
  count: number;
  empty: string;
  children: React.ReactNode;
}> = ({ title, tone, count, empty, children }) => {
  const badge = tone === 'amber' ? 'text-amber-600' : 'text-red-600';
  return (
    <Panel>
      <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
        <span className="text-sm font-bold text-ink">{title}</span>
        <span className={`text-sm font-bold tabular ${count ? badge : 'text-gray-300'}`}>
          {count}
        </span>
      </div>
      {count === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-gray-400">
          {empty}
        </div>
      ) : (
        <ul>{children}</ul>
      )}
    </Panel>
  );
};
